const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_TIMEOUT = 30 * 60 * 1000;
const FIRST_TOUCH_TTL = 90 * 24 * 60 * 60 * 1000;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid'];

const read = (key) => {
  try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
};
const write = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ }
};
const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`);

export const getVisitorId = () => {
  let id = localStorage.getItem('cm-visitor-id');
  if (!id) {
    id = newId();
    localStorage.setItem('cm-visitor-id', id);
  }
  return id;
};

const currentAttribution = () => {
  const params = new URLSearchParams(window.location.search);
  const attr = {
    landing_page: `${window.location.pathname}${window.location.search}`.slice(0, 500),
    referrer: document.referrer ? document.referrer.slice(0, 500) : null,
    first_seen: new Date().toISOString(),
  };
  UTM_KEYS.forEach((k) => { if (params.get(k)) attr[k] = params.get(k).slice(0, 200); });
  return attr;
};

const hasCampaign = () => UTM_KEYS.some((k) => new URLSearchParams(window.location.search).get(k));

// Session = 30 min of inactivity; a new campaign click also starts a new session
export const getSession = () => {
  const now = Date.now();
  let session = read('cm-session');
  if (!session || now - session.last > SESSION_TIMEOUT || (hasCampaign() && !session.fresh)) {
    session = { id: newId(), last: now, attr: currentAttribution(), pages: [], fresh: true };
  }
  session.last = now;
  write('cm-session', { ...session, fresh: false });
  const first = read('cm-first-touch');
  if (!first || now - new Date(first.first_seen).getTime() > FIRST_TOUCH_TTL) write('cm-first-touch', session.attr);
  return session;
};

const send = (payload) => {
  fetch(`${API}/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
};

let lastPageview = { path: null, at: 0 };

export const recordPageview = (path) => {
  // Ignore the duplicate effect run React StrictMode triggers in development
  if (lastPageview.path === path && Date.now() - lastPageview.at < 1000) return;
  lastPageview = { path, at: Date.now() };
  // Kept on this device only, attached to a lead if the visitor submits the form
  const session = getSession();
  const pages = [...(session.pages || []), path].slice(-60);
  write('cm-session', { ...session, pages, fresh: false });
};

export const adCreative = (image) => image.split('/').pop().replace(/\.[a-z]+$/i, '');

// Last ad clicked in this visit gets credit for a phone tap or form booking
export const trackAdClick = (slug, image, placement) => {
  const session = getSession();
  const ad = { slug, creative: adCreative(image), placement, at: new Date().toISOString() };
  write('cm-session', { ...session, ad, fresh: false });
  send({ type: 'ad_click', visitor_id: getVisitorId(), session_id: session.id, path: window.location.pathname, attribution: session.attr, ad });
  if (window.gtag) window.gtag('event', 'select_promotion', { promotion_id: slug, creative_name: ad.creative, creative_slot: placement });
};

export const trackPhoneClick = (path) => {
  const session = getSession();
  send({ type: 'phone_click', visitor_id: getVisitorId(), session_id: session.id, path, attribution: session.attr, ad: session.ad || null });
  // GA4 event without any personal data
  if (window.gtag) window.gtag('event', 'phone_click', { page_path: path });
};

// Context attached to contact-form submissions
export const getLeadContext = () => {
  const session = read('cm-session') || getSession();
  const pages = session.pages || [];
  return {
    page_url: `${window.location.pathname}${window.location.search}`.slice(0, 500),
    previous_page: pages.length > 1 ? pages[pages.length - 2] : null,
    pages_visited: pages,
    attribution: session.attr || null,
    first_touch: read('cm-first-touch'),
    ad: Date.now() - session.last <= SESSION_TIMEOUT ? session.ad || null : null,
  };
};

export { newId };
